/**
 * @ipc/core · src/rbac.ts — RBAC-движок и каталог прав платёжного центра.
 * Раздел 09 (RBAC). Провайдеро-агностично: не знает про Multicard/провайдеров.
 *
 * Движок `hasPermissionWithWildcard` + `buildImpliesClosure`/`getImpliesClosure`/
 * `__resetImpliesClosureForTests` — перенесены БЕЗ ИЗМЕНЕНИЯ ЛОГИКИ из
 * masters-club-bot/apps/backend/src/middleware/permission.guard.ts (строка ~106).
 *
 * Каталог прав и системные роли — написаны заново под домен платёжки.
 *
 * КАНОН (DECISIONS-canon.md, применяется единообразно во всех файлах):
 * право на возврат — ЕДИНОЕ `refunds.create`. `payments.refund` НЕ вводится
 * нигде (упоминания payments.refund в DoD-тексте/finance-роли тела
 * tech-spec/09-rbac.md — рудимент более раннего черновика; сам инлайн-контракт
 * `SYSTEM_PERMISSIONS`/`ALL_PERMISSIONS` спеки уже НЕ включает payments.refund).
 * Итог: 16 прав в 9 категориях по канону спеки (совпадает с инлайн-массивом
 * спеки; текстовое "18 прав" в DoD того же файла — рассинхрон, канон и счёт по
 * факту массива приоритетны). Аддитивно добавлено служебное право
 * `tenants.read` (категория tenants) для экрана списка тенантов admin-ui —
 * итого 17 прав в 10 категориях. Существующие 16 прав и 5 ролей не изменены.
 * Далее аддитивно добавлено право `admins.manage` (категория admins) под переход
 * на Telegram-аутентификацию: заведение операторов по tgUsername и привязка
 * telegramId — итого 18 прав в 11 категориях.
 * Далее аддитивно `tenants.manage` (implies tenants.read) — правка метаданных
 * тенанта и привязка администраторов к тенанту из admin-ui — итого 19 прав.
 */

// ==================== Каталог категорий ====================

export const PERMISSION_CATEGORIES = {
  INVOICES: 'invoices',
  LINKS: 'links',
  PAYMENTS: 'payments',
  REFUNDS: 'refunds',
  ANALYTICS: 'analytics',
  LOGS: 'logs',
  WEBHOOKS: 'webhooks',
  MERCHANTS: 'merchants',
  ROLES: 'roles',
  ADMINS: 'admins',
  TENANTS: 'tenants',
} as const;

export type PermissionCategory = (typeof PERMISSION_CATEGORIES)[keyof typeof PERMISSION_CATEGORIES];

export interface PermissionDefinition {
  code: string;
  name: string;
  description?: string;
  category: PermissionCategory;
  /** Права, автоматически подразумеваемые этим правом (транзитивно). */
  implies?: string[];
}

// ==================== Каталог кодов ====================

export const SYSTEM_PERMISSIONS = {
  INVOICES_READ: 'invoices.read',
  INVOICES_CREATE: 'invoices.create',
  INVOICES_CANCEL: 'invoices.cancel',
  LINKS_READ: 'links.read',
  LINKS_CREATE: 'links.create',
  LINKS_CANCEL: 'links.cancel',
  PAYMENTS_READ: 'payments.read',
  REFUNDS_READ: 'refunds.read',
  REFUNDS_CREATE: 'refunds.create',
  ANALYTICS_READ: 'analytics.read',
  LOGS_READ: 'logs.read',
  WEBHOOKS_READ: 'webhooks.read',
  WEBHOOKS_MANAGE: 'webhooks.manage',
  MERCHANTS_READ: 'merchants.read',
  MERCHANTS_MANAGE: 'merchants.manage',
  ROLES_MANAGE: 'roles.manage',
  ADMINS_MANAGE: 'admins.manage',
  TENANTS_READ: 'tenants.read',
  TENANTS_MANAGE: 'tenants.manage',
} as const;

export type SystemPermission = (typeof SYSTEM_PERMISSIONS)[keyof typeof SYSTEM_PERMISSIONS];

/** Полный каталог прав платёжки: 19 прав, 11 категорий. Домен клуба НЕ переносится. */
export const ALL_PERMISSIONS: PermissionDefinition[] = [
  // ==================== INVOICES (инвойсы) ====================
  {
    code: 'invoices.read',
    name: 'Просмотр инвойсов',
    category: 'invoices',
    description: 'Просмотр списка инвойсов и их статусов',
  },
  {
    code: 'invoices.create',
    name: 'Создание инвойсов',
    category: 'invoices',
    description: 'Создание инвойсов через провайдера',
    implies: ['invoices.read'],
  },
  {
    code: 'invoices.cancel',
    name: 'Аннулирование инвойсов',
    category: 'invoices',
    description: 'Аннулирование неоплаченного инвойса',
    implies: ['invoices.read'],
  },

  // ==================== LINKS (платёжные ссылки) ====================
  {
    code: 'links.read',
    name: 'Просмотр ссылок оплаты',
    category: 'links',
    description: 'Просмотр созданных ручных ссылок оплаты',
  },
  {
    code: 'links.create',
    name: 'Создание ссылок оплаты',
    category: 'links',
    description: 'Ручное создание платёжной ссылки (checkout_url/short_link/QR)',
    implies: ['links.read', 'invoices.read'],
  },
  {
    code: 'links.cancel',
    name: 'Аннулирование ссылок',
    category: 'links',
    description: 'Аннулирование ранее созданной ссылки/инвойса',
    implies: ['links.read'],
  },

  // ==================== PAYMENTS (платежи) ====================
  {
    code: 'payments.read',
    name: 'Просмотр платежей',
    category: 'payments',
    description: 'Просмотр платежей и их статусов',
  },

  // ==================== REFUNDS (возвраты) ====================
  {
    code: 'refunds.read',
    name: 'Просмотр возвратов',
    category: 'refunds',
    description: 'Просмотр истории возвратов',
  },
  {
    code: 'refunds.create',
    name: 'Оформление возвратов',
    category: 'refunds',
    description: 'Полный/частичный возврат с перефискализацией (единое право на возврат)',
    implies: ['refunds.read', 'payments.read'],
  },

  // ==================== ANALYTICS (аналитика) ====================
  {
    code: 'analytics.read',
    name: 'Просмотр аналитики',
    category: 'analytics',
    description: 'Сводки по платежам, конверсии, объёмам',
  },

  // ==================== LOGS (сырые логи) ====================
  {
    code: 'logs.read',
    name: 'Просмотр сырых логов',
    category: 'logs',
    description: 'Просмотр WebhookLog и PaymentEvent timeline',
  },

  // ==================== WEBHOOKS (исходящие вебхуки) ====================
  {
    code: 'webhooks.read',
    name: 'Просмотр вебхуков',
    category: 'webhooks',
    description: 'Просмотр подписок и логов доставки исходящих вебхуков',
  },
  {
    code: 'webhooks.manage',
    name: 'Управление вебхуками',
    category: 'webhooks',
    description: 'Создание/изменение подписок, ретрай доставки',
    implies: ['webhooks.read'],
  },

  // ==================== MERCHANTS (мерчанты) ====================
  {
    code: 'merchants.read',
    name: 'Просмотр мерчантов',
    category: 'merchants',
    description: 'Просмотр мерчантов тенанта',
  },
  {
    code: 'merchants.manage',
    name: 'Управление мерчантами',
    category: 'merchants',
    description: 'Создание/настройка мерчантов; снимает per-merchant scope',
    implies: ['merchants.read'],
  },

  // ==================== ROLES (роли и права) ====================
  {
    code: 'roles.manage',
    name: 'Управление ролями',
    category: 'roles',
    description: 'Создание/изменение ролей и назначение прав',
  },

  // ==================== ADMINS (операторы служебного UI) ====================
  {
    code: 'admins.manage',
    name: 'Управление администраторами',
    category: 'admins',
    description: 'Заведение операторов по Telegram username, назначение роли, активация/деактивация',
  },

  // ==================== TENANTS (тенанты) ====================
  {
    code: 'tenants.read',
    category: 'tenants',
    name: 'Просмотр тенантов',
    description: 'Список тенантов, карточка, аналитика по тенанту',
  },
  {
    code: 'tenants.manage',
    category: 'tenants',
    name: 'Управление тенантами',
    description: 'Редактирование метаданных тенанта и привязка администраторов к тенанту',
    implies: ['tenants.read'],
  },
];

// ==================== Системные роли ====================

export interface RoleDefinition {
  /** Машинное имя (стабильный идентификатор системной роли). */
  name: string;
  displayName: string;
  description: string;
  permissions: string[];
}

export const SYSTEM_ROLES: RoleDefinition[] = [
  {
    name: 'admin',
    displayName: 'Администратор',
    description: 'Полный доступ ко всем функциям платёжного центра',
    permissions: ['*'], // wildcard-супердоступ; hasPermissionWithWildcard('*') → true
  },
  {
    name: 'operator',
    displayName: 'Оператор',
    description: 'Создание платёжных ссылок и инвойсов',
    permissions: [
      'invoices.read',
      'invoices.create',
      'invoices.cancel',
      'links.read',
      'links.create',
      'links.cancel',
      'payments.read',
      'merchants.read',
    ],
  },
  {
    name: 'finance',
    displayName: 'Финансист',
    description: 'Возвраты и работа с платежами',
    permissions: ['payments.read', 'refunds.read', 'refunds.create', 'invoices.read', 'merchants.read'],
  },
  {
    name: 'analyst',
    displayName: 'Аналитик',
    description: 'Аналитика и чтение логов',
    permissions: ['analytics.read', 'logs.read', 'payments.read', 'refunds.read', 'merchants.read'],
  },
  {
    name: 'support',
    displayName: 'Поддержка',
    description: 'Доступ только на чтение',
    permissions: ['invoices.read', 'links.read', 'payments.read', 'refunds.read', 'webhooks.read', 'merchants.read'],
  },
];

// ==================== Движок hasPermissionWithWildcard (перенос как есть) ====================

/**
 * Lazy-built map: code -> транзитивное замыкание implies (рекурсивное раскрытие).
 * Защищён от циклов через visited set. Если у permission нет implies — ключ
 * отсутствует в Map, проверка через implies просто пропускается.
 */
let impliesClosureCache: Map<string, string[]> | null = null;

interface PermissionDefinitionWithImplies {
  code: string;
  implies?: string[];
}

/**
 * Строит транзитивное implies-замыкание из произвольного списка определений прав.
 * Чистая функция (без обращения к модульному ALL_PERMISSIONS) — вынесена отдельно
 * от `buildImpliesClosure()`, чтобы DFS-устойчивость к циклам была тестируема
 * напрямую на синтетическом графе (см. test/rbac-cycle.test.ts), без mock.module
 * (который в Bun подменяет модуль process-wide и не подходит для точечного теста
 * одного файла в сьюте пакета).
 */
export function buildImpliesClosureFromDefs(defs: PermissionDefinitionWithImplies[]): Map<string, string[]> {
  // Прямой граф: code -> implies[]
  const directGraph = new Map<string, string[]>();
  for (const perm of defs) {
    const direct = perm.implies;
    if (Array.isArray(direct) && direct.length > 0) {
      directGraph.set(perm.code, direct);
    }
  }

  const closure = new Map<string, string[]>();

  // DFS с защитой от циклов через visited
  function expand(code: string, visited: Set<string>, acc: Set<string>): void {
    const direct = directGraph.get(code);
    if (!direct) {
      return;
    }
    for (const next of direct) {
      if (visited.has(next)) {
        continue;
      }
      visited.add(next);
      acc.add(next);
      expand(next, visited, acc);
    }
  }

  for (const code of directGraph.keys()) {
    const acc = new Set<string>();
    const visited = new Set<string>([code]);
    expand(code, visited, acc);
    if (acc.size > 0) {
      closure.set(code, Array.from(acc));
    }
  }

  return closure;
}

function buildImpliesClosure(): Map<string, string[]> {
  return buildImpliesClosureFromDefs(ALL_PERMISSIONS as PermissionDefinitionWithImplies[]);
}

function getImpliesClosure(): Map<string, string[]> {
  if (impliesClosureCache === null) {
    impliesClosureCache = buildImpliesClosure();
  }
  return impliesClosureCache;
}

/**
 * Сбрасывает кэш implies-closure. Использовать ТОЛЬКО в тестах после подмены
 * ALL_PERMISSIONS, чтобы перестроить замыкание.
 */
export function __resetImpliesClosureForTests(): void {
  impliesClosureCache = null;
}

/**
 * Проверяет наличие права с поддержкой wildcards и транзитивных implies.
 * Поддерживает:
 * - Super admin: "*" даёт доступ ко всему
 * - Admin wildcard: "admin.*" даёт доступ ко всему
 * - Точное совпадение: "invoices.read" === "invoices.read"
 * - Префиксный wildcard: "refunds.*" даёт доступ к "refunds.read", "refunds.create" и т.д.
 * - Транзитивные implies: если permission X implies Y и Z, и у юзера есть X —
 *   проверки Y/Z тоже проходят. Раскрытие рекурсивное (с защитой от циклов).
 */
export function hasPermissionWithWildcard(permissions: string[], requiredCode: string): boolean {
  // Super admin имеет все права
  if (permissions.includes('*') || permissions.includes('admin.*')) {
    return true;
  }

  // Точное совпадение
  if (permissions.includes(requiredCode)) {
    return true;
  }

  // Проверка wildcard для категории права
  // Поддерживает оба разделителя: "." и ":"
  const parts = requiredCode.split(/[.:]/);
  const separator = requiredCode.includes(':') ? ':' : '.';
  for (let i = parts.length - 1; i > 0; i--) {
    const wildcardCode = parts.slice(0, i).join(separator) + separator + '*';
    if (permissions.includes(wildcardCode)) {
      return true;
    }
  }

  // Транзитивные implies: если у юзера есть X, и X транзитивно раскрывается
  // в requiredCode → доступ есть.
  const closure = getImpliesClosure();
  if (closure.size > 0) {
    for (const held of permissions) {
      const implied = closure.get(held);
      if (implied && implied.includes(requiredCode)) {
        return true;
      }
    }
  }

  return false;
}
